import axios from "axios";

export const AUTH_KEY = "di_brownie_session_user_v2";
const API_BASE = String(import.meta.env.VITE_API_BASE_URL || "").replace(/\/+$/, "");

axios.defaults.withCredentials = true;
let refreshPromise = null;
let redirectingToLogin = false;

const saveUser = (user) => {
  try {
    if (user) localStorage.setItem(AUTH_KEY, JSON.stringify(user));
    else localStorage.removeItem(AUTH_KEY);
  } catch {}
  window.dispatchEvent(new CustomEvent("dibrownie-auth-change", { detail: user || null }));
  return user || null;
};

const isAuthenticationRequest = (url = "") => /\/api\/auth\/(login|me|logout)(\?|$)/.test(String(url));

const handleUnauthorized = (url = "") => {
  if (isAuthenticationRequest(url) || redirectingToLogin) return;
  saveUser(null);
  redirectingToLogin = true;
  window.dispatchEvent(new CustomEvent("dibrownie-session-expired"));
  window.setTimeout(() => {
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const target = `/login?returnTo=${encodeURIComponent(returnTo)}`;
    window.location.assign(target);
  }, 50);
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) handleUnauthorized(error?.config?.url);
    return Promise.reject(error);
  }
);

export async function authenticatedFetch(input, init = {}) {
  const response = await fetch(input, {
    ...init,
    credentials: "include",
  });
  if (response.status === 401) handleUnauthorized(typeof input === "string" ? input : input?.url);
  return response;
}

export function getCurrentUser() {
  try {
    const parsed = JSON.parse(localStorage.getItem(AUTH_KEY) || "null");
    return parsed?.id ? parsed : null;
  } catch { return null; }
}

export function isAuthed() { return Boolean(getCurrentUser()); }

export function hasPermission(permission, user = getCurrentUser()) {
  if (!permission) return Boolean(user);
  return Boolean(user?.permissions?.includes(permission));
}

export const permissionPaths = [
  ["overview", "/visao-geral"], ["finance", "/financeiro"], ["stock", "/inventory-analytics-dashboard"],
  ["finished_stock", "/estoque-produto-acabado"], ["pcp", "/pcp"], ["mercos_routes", "/mercos-rotas-dashboard"],
  ["manual_routes", "/mercos-rotas-planejadas"], ["ipdv_promoter", "/promotor-vendas"], ["commercial", "/comercial"],
  ["commercial_budget", "/budget-comercial"], ["tastings", "/controle-degustacao"], ["tasks", "/tarefas"], ["admin", "/administracao"],
  ["processes", "/processos"],
  ["super_jhonny", "/super-jhonny"],
];

export function getDefaultPath(user = getCurrentUser()) {
  if (user?.userType === "sales_promoter") return "/promotor-vendas";
  return permissionPaths.find(([permission]) => hasPermission(permission, user))?.[1] || "/sem-acesso";
}

export async function login(username, password) {
  try {
    const { data } = await axios.post(`${API_BASE}/api/auth/login`, { username, password }, { timeout: 20000, withCredentials: true });
    return saveUser(data?.user || null);
  } catch (error) {
    saveUser(null);
    if (error?.response?.status === 401) return null;
    throw new Error(error?.response?.data?.message || "Não foi possível conectar ao servidor.");
  }
}

export async function refreshSession({ force = false } = {}) {
  if (refreshPromise && !force) return refreshPromise;
  refreshPromise = axios.get(`${API_BASE}/api/auth/me`, { timeout: 15000, withCredentials: true })
    .then(({ data }) => saveUser(data?.user || null))
    .catch(() => saveUser(null))
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
}

export async function logout() {
  try { await axios.post(`${API_BASE}/api/auth/logout`, {}, { timeout: 10000, withCredentials: true }); } catch {}
  saveUser(null);
}
